import { createEntityAdapter, createSlice, EntityId, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import { NotificationProps, NotificationsState } from './notifications.types';
import { notificationsApiSlice } from './notificationsApi';

const notificationsAdapter = createEntityAdapter<NotificationProps, EntityId>({
  selectId: (notification) => notification._id,
  sortComparer: (a, b) => b.createdAt?.localeCompare(a.createdAt)
});

const initialState: NotificationsState = {
  notificationsList: notificationsAdapter.getInitialState(),
  totalNotificationsCount: 0,
  unreadNotificationsCount: 0
};

const notificationsSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addMatcher(
        notificationsApiSlice.endpoints.getNotifications.matchFulfilled,
        (state, { payload }) => {
          notificationsAdapter.upsertMany(state.notificationsList, payload.data);
          state.totalNotificationsCount = payload.info.totalCount;
        }
      )
      .addMatcher(
        notificationsApiSlice.endpoints.getNotificationsUnreadCount.matchFulfilled,
        (state, { payload }) => {
          state.unreadNotificationsCount = payload.unreadNotificationsCount;
        }
      )
      .addMatcher(notificationsApiSlice.endpoints.markSeenNotifications.matchFulfilled, (state) => {
        state.unreadNotificationsCount = 0;
      })
      .addMatcher(
        notificationsApiSlice.endpoints.clearNotification.matchFulfilled,
        (state, { payload: id }) => {
          notificationsAdapter.removeOne(state.notificationsList, id);
        }
      )
      .addMatcher(notificationsApiSlice.endpoints.clearAllNotifications.matchFulfilled, (state) => {
        notificationsAdapter.removeAll(state.notificationsList);
      });
  },
  initialState,
  name: 'notifications',
  reducers: {
    addNewNotification: (state, action: PayloadAction<NotificationProps>) => {
      state.unreadNotificationsCount += 1;
      notificationsAdapter.addOne(state.notificationsList, action.payload);
    },
    clearAllNotifications: (state) => {
      notificationsAdapter.removeAll(state.notificationsList);
    },
    resetNotifications: (state) => {
      state.notificationsList = notificationsAdapter.getInitialState();
    }
  }
});

export const notificationsSelectors = notificationsAdapter.getSelectors<RootState>(
  (state) => state.notifications.notificationsList
);

export const selectNotifications = (state: RootState) => state.notifications;

export const { addNewNotification, clearAllNotifications, resetNotifications } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;
